import React from 'react'
import _ from 'lodash'
import { WithMobileHeader } from '../../../contexts/mobile-header-context'
import { ImageLoader } from 'browser/components/atomic-elements/atoms/image-loader/image-loader'
import apis from 'browser/app/models/apis'
import { PlatformType } from 'shared-libs/models/types/storyboard/storyboard-plan'

import { getLogoUri } from '../utils'
import DriverCheckin from './driver-checkin'

import './../_kiosk.scss'
import DriverCheckinDetails from './driver-checkin-details'
import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'

export enum CheckInStep {
  DriverCheckin = 'driver-signin',
  DriverCheckinDetails = 'driver-details',
}

export interface IKioskContext {
  [key: string]: unknown

  phoneNumber?: string
}

export interface ICheckinProps {
  containerId: string
  facilityId?: string
  storyId?: string
}

/** Props passed down to each individual step component (e.g., DriverCheckin). */
export interface IKioskComponentProps extends IBaseProps {
  value?: any
  context: IKioskContext
  onNext: (data: IKioskContext) => void
  onChange?: (value: any, event?: any, silentUpdate?: boolean) => void
  setTitle?: (title: any) => void
}

/** The internal state of the main CheckInFlow component. */
interface ICheckInFlowState {
  currStep: CheckInStep
  context: IKioskContext
  headerTitle?: string
  history?: CheckInStep[]
}

/**
 * Kiosk flow configuration
 */
type RouteConfigMap = Record<CheckInStep, { component: any; nextStep?: CheckInStep | null }>
const RouteMap: RouteConfigMap = {
  [CheckInStep.DriverCheckin]: {
    component: DriverCheckin,
    nextStep: CheckInStep.DriverCheckinDetails,
  },
  [CheckInStep.DriverCheckinDetails]: {
    component: DriverCheckinDetails,
    nextStep: null,
  },
}

export class CheckInFlow extends React.Component<ICheckinProps, ICheckInFlowState> {
  public static defaultProps: Partial<ICheckinProps> = {
    storyId: 'driver_story',
  }

  constructor(props: ICheckinProps) {
    super(props)

    apis?.getSettings().setPlatform(PlatformType.KIOSK)

    const checkinDetails = this.getCheckinDetails()

    this.state = {
      currStep: CheckInStep.DriverCheckin,
      context: Object.assign(
        { phoneNumber: '', containerId: props.containerId, storyId: props.storyId },
        checkinDetails
      ),
      history: [],
      headerTitle: 'Welcome',
    }
  }

  public render() {
    return (
      <div className="kiosk-container">
        <WithMobileHeader
          title={this.getTitle()}
          languageButton={{ handler: _.noop }}
          kioskHome="/kiosk/checkin"
        >
          <div className="kiosk-company-logo-container">
            <ImageLoader className="kiosk-company-logo" src={getLogoUri()} />
          </div>
          <div className="kiosk-main-content">{this.renderCurrentStep()}</div>
        </WithMobileHeader>
      </div>
    )
  }

  /********************************************************************************/

  private getTitle = () => {
    return this.state.headerTitle ?? ''
  }

  private getCheckinDetails = () => {
    const user = apis.getStore().getUser()
    const containerId = _.get(
      user,
      "user.facility.denormalizedProperties['core_yms_facility.checkInPlanContainer'].entityId",
      this.props.containerId
    )

    return {
      containerId,
    }
  }
  /********************************************************************************/
  /**
   * View render
   */
  private renderCurrentStep() {
    const { currStep, context } = this.state

    const routeConfig = RouteMap[currStep]

    if (!routeConfig) {
      console.error(`No configuration found for step: ${currStep}`)
      return <div>An error has occurred.</div>
    }

    const KioskComponent = routeConfig.component

    return <KioskComponent context={context} onNext={this.handleNext} setTitle={this.setTitle} />
  }

  /********************************************************************************/
  /**
   *  Callbacks
   */
  private handleNext = (inputContext: IKioskContext = {}) => {
    const { currStep, context } = this.state

    const nextStep = RouteMap[currStep]?.nextStep

    if (!nextStep) {
      console.log('End of flow or next step not defined.')
      // Handle end-of-flow logic here
      return
    }

    this.setState({
      currStep: nextStep,
      // Merge the new data into the existing context
      context: { ...context, ...inputContext },
    })
  }

  private setTitle = (headerTitle: string) => {
    this.setState({ headerTitle })
  }
}
